import React, { useCallback, useMemo, useRef, useState } from "react";
import {
    IsValidConnection,
    NodeChange,
    OnConnect,
    OnNodeDrag,
    ReactFlow,
    XYPosition,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader } from "@design-system/Loader/Loader";
import {
    CANVAS_GRID,
    ENTITY_NODE_HEIGHT,
    ENTITY_NODE_WIDTH,
    OwnershipFlowEdge,
    OwnershipFlowNode,
    OwnershipSelection,
    SELECTED_EDGE_Z,
} from "../ownership.types";
import { selectionForEdge, unorderedPairKey } from "../ownershipGraph.helpers";
import {
    getAbsoluteNodeRects,
    NodeRect,
} from "../ownershipEdgeRouting.helpers";
import {
    CollisionBounds,
    resolveNodeCollision,
} from "../ownershipHelperLines.helpers";
import { EdgeSelectionContext } from "./edgeSelection.context";
import { EntityNode } from "./EntityNode";
import { GroupNode } from "./GroupNode";
import { GroupHeaderNode } from "./GroupHeaderNode";
import { OwnershipConnectionLine } from "./OwnershipConnectionLine";
import { OwnershipEdge } from "./OwnershipEdge";
import { useOwnershipFitView } from "./useOwnershipFitView";
import { useSyncedFlowNodes } from "./useSyncedFlowNodes";
import styles from "./OwnershipCanvas.module.scss";

const nodeTypes = {
    entityNode: EntityNode,
    groupNode: GroupNode,
    groupHeaderNode: GroupHeaderNode,
};

const edgeTypes = {
    ownershipEdge: OwnershipEdge,
};

// Grid dragged nodes snap to, so hand-placed layouts stay aligned. Shares the
// node's base unit (CANVAS_GRID) so card edges/centers land on the grid.
const SNAP_GRID: [number, number] = [CANVAS_GRID, CANVAS_GRID];

// Clearance between members inside a group box — one grid step, tighter than
// the open-canvas gap since a box has little room.
const IN_GROUP_COLLISION_GAP = CANVAS_GRID;

function isEdgeSelected(
    edge: OwnershipFlowEdge,
    selection: OwnershipSelection,
): boolean {
    if (selection?.kind === "relationship") {
        return (
            selection.relationshipId === edge.id ||
            (edge.data?.bundledRelationships ?? []).some(
                ({ id }) => id === selection.relationshipId,
            )
        );
    }
    if (selection?.kind === "relationshipBundle") {
        return (
            (edge.data?.bundledRelationships ?? []).length > 1 &&
            unorderedPairKey(edge.source, edge.target) ===
                unorderedPairKey(
                    selection.sourceEntityUuid,
                    selection.targetEntityUuid,
                )
        );
    }
    return false;
}

interface NodeDragMove {
    id: string;
    position: XYPosition;
}

function groupHeaderId(groupNodeId: string): string {
    return `${groupNodeId}:header`;
}

interface OwnershipCanvasProps {
    nodes: OwnershipFlowNode[];
    edges: OwnershipFlowEdge[];
    isLoading: boolean;
    selection: OwnershipSelection;
    onSelectionChange: (selection: OwnershipSelection) => void;
    onNodesDragStart: () => void;
    onNodesDrag: (moves: readonly NodeDragMove[]) => void;
    onNodesDragStop: (moves: readonly NodeDragMove[]) => void;
    onConnect: OnConnect;
    isValidConnection: IsValidConnection;
}

export const OwnershipCanvas: React.FC<OwnershipCanvasProps> = ({
    nodes,
    edges,
    isLoading,
    selection,
    onSelectionChange,
    onNodesDragStart,
    onNodesDrag,
    onNodesDragStop,
    onConnect,
    isValidConnection,
}) => {
    const { rfNodes, onNodesChange, isDraggingRef, startDrag, endDrag } =
        useSyncedFlowNodes(nodes);
    const [isFlowReady, setIsFlowReady] = useState(false);
    // The snapped positions actually shown on the last drag frame — committed
    // verbatim on release so the node stays exactly where the user sees it
    // (not React Flow's raw, pre-snap drag-stop position).
    const latestDragMovesRef = useRef<NodeDragMove[]>([]);

    useOwnershipFitView({ nodes, edges, isFlowReady });

    const rfNodesById = useMemo(
        () => new Map(rfNodes.map((node) => [node.id, node])),
        [rfNodes],
    );

    // Where a dragged node should sit: push out of overlap, then snap to the
    // grid. One resolver for both frames — a top-level node collides against
    // absolute rects of everything else; a group member collides against its
    // siblings in the group's relative frame and is clamped inside the box.
    const resolvePlacement = useCallback(
        (node: OwnershipFlowNode, candidate: XYPosition): XYPosition => {
            const size = {
                width: node.width ?? ENTITY_NODE_WIDTH,
                height: node.height ?? ENTITY_NODE_HEIGHT,
            };
            const parent = node.parentId
                ? rfNodesById.get(node.parentId)
                : undefined;

            let others: NodeRect[];
            let gap: number | undefined;
            let bounds: CollisionBounds | undefined;
            if (parent) {
                others = rfNodes
                    .filter(
                        (other) =>
                            other.id !== node.id &&
                            other.parentId === node.parentId &&
                            other.type === "entityNode",
                    )
                    .map((other) => ({
                        x: other.position.x,
                        y: other.position.y,
                        width: other.width ?? ENTITY_NODE_WIDTH,
                        height: other.height ?? ENTITY_NODE_HEIGHT,
                    }));
                gap = IN_GROUP_COLLISION_GAP;
                // Keep the member inside its box so collision can't pick an
                // escape the clamp would just undo (back onto a sibling).
                bounds = {
                    minX: 0,
                    minY: 0,
                    maxX: Math.max(
                        (parent.width ?? size.width) - size.width,
                        0,
                    ),
                    maxY: Math.max(
                        (parent.height ?? size.height) - size.height,
                        0,
                    ),
                };
            } else {
                const rects = getAbsoluteNodeRects(rfNodes);
                others = rfNodes
                    .filter(
                        (other) =>
                            other.id !== node.id &&
                            other.parentId !== node.id &&
                            other.type !== "groupHeaderNode",
                    )
                    .flatMap((other) => {
                        const rect = rects.get(other.id);
                        return rect ? [rect] : [];
                    });
            }

            const separated = resolveNodeCollision({
                active: { x: candidate.x, y: candidate.y, ...size },
                others,
                gap,
                bounds,
            });
            // Final grid snap (collision push can land between grid lines),
            // then re-clamp so the snap can't nudge a member out of its box.
            let x = Math.round(separated.x / CANVAS_GRID) * CANVAS_GRID;
            let y = Math.round(separated.y / CANVAS_GRID) * CANVAS_GRID;
            if (bounds) {
                x = Math.min(Math.max(x, bounds.minX), bounds.maxX);
                y = Math.min(Math.max(y, bounds.minY), bounds.maxY);
            }
            return { x, y };
        },
        [rfNodes, rfNodesById],
    );

    // Drag lives in onNodesChange. A lone top-level node snaps to grid + out of
    // collisions; a member collides against its siblings inside the box; a
    // dragged group carries its header pill. Every position change is lifted to
    // the page so the edges re-route against the live spot.
    const handleNodesChange = useCallback(
        (changes: Array<NodeChange<OwnershipFlowNode>>) => {
            // Snap for the whole gesture (incl. the final dragging:false frame)
            // so the stored position never reverts to React Flow's raw one.
            const isGesture = isDraggingRef.current;
            const positionChanges = changes.filter(
                (change) => change.type === "position" && change.position,
            );
            const isSingleDrag = isGesture && positionChanges.length === 1;
            const extra: Array<NodeChange<OwnershipFlowNode>> = [];

            const processed = changes.map((change) => {
                if (change.type !== "position" || !change.position) {
                    return change;
                }
                const node = rfNodesById.get(change.id);
                if (!node) {
                    return change;
                }
                if (node.type === "groupNode") {
                    extra.push({
                        id: groupHeaderId(change.id),
                        type: "position",
                        position: change.position,
                        dragging: change.dragging,
                    });
                }
                if (isSingleDrag && node.type === "entityNode") {
                    return {
                        ...change,
                        position: resolvePlacement(node, change.position),
                    };
                }
                return change;
            });

            const applied = [...processed, ...extra];
            onNodesChange(applied);
            const moves = applied.flatMap((change) =>
                change.type === "position" &&
                change.position &&
                !change.id.endsWith(":header")
                    ? [{ id: change.id, position: change.position }]
                    : [],
            );
            if (isGesture && moves.length > 0) {
                latestDragMovesRef.current = moves;
                onNodesDrag(moves);
            }
        },
        [
            rfNodesById,
            resolvePlacement,
            onNodesChange,
            onNodesDrag,
            isDraggingRef,
        ],
    );

    const handleNodeDragStart = useCallback<
        OnNodeDrag<OwnershipFlowNode>
    >(() => {
        startDrag();
        latestDragMovesRef.current = [];
        onNodesDragStart();
    }, [startDrag, onNodesDragStart]);

    const handleNodeDragStop = useCallback<OnNodeDrag<OwnershipFlowNode>>(
        (_event, node, dragged) => {
            // Commit the snapped positions we last showed, not RF's raw ones,
            // so nothing shifts on release.
            const snapped = latestDragMovesRef.current;
            let moves = snapped;
            if (snapped.length === 0) {
                const list = dragged.length ? dragged : [node];
                moves = list.map(({ id, position }) => ({ id, position }));
            }
            onNodesDragStop(moves);
            endDrag();
            latestDragMovesRef.current = [];
        },
        [onNodesDragStop, endDrag],
    );

    const decoratedNodes = useMemo(
        () =>
            rfNodes.map((node) => {
                if (node.type === "groupHeaderNode") {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            isSelected:
                                selection?.kind === "group" &&
                                selection.groupId === node.data.group.id,
                        },
                    };
                }
                return {
                    ...node,
                    selected:
                        selection?.kind === "entity" &&
                        selection.entityUuid === node.id,
                };
            }),
        [rfNodes, selection],
    );

    const decoratedEdges = useMemo(
        () =>
            edges.map((edge) => {
                const selected = isEdgeSelected(edge, selection);
                return {
                    ...edge,
                    selected,
                    // Lift the selected edge above overlapping edges so its
                    // recolored arrowheads (which paint with the edge path)
                    // aren't hidden under another edge's head. Stays below the
                    // consolidated pill (1000) and label layer (1100).
                    zIndex: selected ? SELECTED_EDGE_Z : edge.zIndex,
                };
            }),
        [edges, selection],
    );

    const handleNodeClick = useCallback(
        (_event: React.MouseEvent, node: OwnershipFlowNode) => {
            if (node.type === "entityNode") {
                onSelectionChange({ kind: "entity", entityUuid: node.id });
            }
        },
        [onSelectionChange],
    );

    const handleEdgeClick = useCallback(
        (_event: React.MouseEvent, edge: OwnershipFlowEdge) => {
            onSelectionChange(selectionForEdge(edge));
        },
        [onSelectionChange],
    );

    const handlePaneClick = useCallback(() => {
        onSelectionChange(null);
    }, [onSelectionChange]);

    return (
        <div className={styles.canvas}>
            <EdgeSelectionContext.Provider value={onSelectionChange}>
                <ReactFlow
                    proOptions={{ hideAttribution: true }}
                    nodes={decoratedNodes}
                    edges={decoratedEdges}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    onNodeClick={handleNodeClick}
                    onEdgeClick={handleEdgeClick}
                    onPaneClick={handlePaneClick}
                    onNodesChange={handleNodesChange}
                    onNodeDragStart={handleNodeDragStart}
                    onNodeDragStop={handleNodeDragStop}
                    onConnect={onConnect}
                    isValidConnection={isValidConnection}
                    connectionLineComponent={OwnershipConnectionLine}
                    nodesDraggable
                    nodeDragThreshold={4}
                    snapToGrid
                    snapGrid={SNAP_GRID}
                    selectNodesOnDrag={false}
                    nodesConnectable
                    elementsSelectable
                    onInit={() => setIsFlowReady(true)}
                    minZoom={0.2}
                    maxZoom={2}
                />
            </EdgeSelectionContext.Provider>
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <Loader />
                </div>
            )}
        </div>
    );
};
